import React, { FC, useEffect, useMemo, useState } from 'react';
import ConfirmDialog, {
  ConfirmDialogProps,
} from '@components/Dialogs/ConfirmDialog';
import { Autocomplete } from '@components/Inputs/Autocomplete';
import { ISelectOption } from '@constants/entities/ui';
import { Alert } from '@mui/material';
import { useGetProjectsMinimalQuery } from '@store/services/projects';
import { transformArrayToOptions } from '@utils/helpers';

export type CopyModelType = 'model' | 'scenario';

type CopyModelToProjectProps = Pick<ConfirmDialogProps, 'open' | 'onClose'> & {
  type: CopyModelType;
  modelName?: string;
  onConfirm: (project: ISelectOption) => void;
};

const titles: Record<CopyModelType, string> = {
  model: 'Copy model to project',
  scenario: 'Copy scenario to project',
};

const descriptions = (name?: string): Record<CopyModelType, string> => ({
  model: `Select a project where you want to copy the model.`,
  scenario: `Select a project where you want to copy the “${name}” scenario.`,
});

const CopyModelToProject: FC<CopyModelToProjectProps> = ({
  type,
  modelName,
  onConfirm,
  ...dialogProps
}) => {
  const [project, setProject] = useState<ISelectOption | null>();
  const [error, setError] = useState<string | null>(null);

  const { data: projectsData } = useGetProjectsMinimalQuery();

  useEffect(() => {
    setProject(null);
    setError(null);
  }, [dialogProps.open]);

  const options = useMemo(() => {
    return transformArrayToOptions(projectsData ?? [], {
      label: 'name',
      value: 'id',
    });
  }, [projectsData]);

  const handleConfirmClick = () => {
    if (!project) {
      return setError('Please select a project');
    }

    onConfirm(project);
  };

  return (
    <>
      <ConfirmDialog
        title={titles[type]}
        text={descriptions(modelName)[type]}
        onConfirm={handleConfirmClick}
        {...dialogProps}
      >
        <Autocomplete
          label="Project name"
          placeholder="Select a project"
          value={project as any}
          onChange={(_, option) => {
            setError(null);
            setProject(option as any);
          }}
          options={options}
          error={!!error}
          helperText={error}
        />

        {project && type === 'model' && (
          <Alert severity="error" variant="filled">
            Copying will overwrite the existing model in {project?.label}.
          </Alert>
        )}
      </ConfirmDialog>
    </>
  );
};

export default CopyModelToProject;
